import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import moment from "moment";
import { DataTable, DeleteEventDialog } from "../../components";
import { getAdminEvents, deleteEvent } from "../../api/events";
import { IEventListItem } from "../../models/IEvent";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useDebouncedSearch } from "../../hooks";
import { Button, EmptyState, Loader, PageHeader, Tag } from "../../ui";
import type { TagTone } from "../../ui";
import "./styles.scss";

const getVisibilityStatus = (event: IEventListItem): "active" | "scheduled" | "hidden" => {
  if (!event.isActive) return "hidden";
  if (event.visibleFrom) {
    const now = moment();
    const from = moment(event.visibleFrom);
    if (now.isBefore(from)) return "scheduled";
  }
  return "active";
};

const Events = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [events, setEvents] = useState<IEventListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<IEventListItem | undefined>();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string>("displayOrder");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const { searchValue, setSearchValue } = useDebouncedSearch(handleSearch, 500, "");

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAdminEvents();
      setEvents(data);
    } catch (err: any) {
      if (err?.message === "No events found") setEvents([]);
      else dispatch(setGeneralAlertMessage({ message: err?.message || "Failed to load events", type: "error" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleEdit = (event: IEventListItem) => {
    navigate(`/dashboard/events/${event.id}/edit`);
  };

  const handleDelete = (event: IEventListItem) => {
    setEventToDelete(event);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;
    try {
      await deleteEvent(eventToDelete.id);
      dispatch(setGeneralAlertMessage({ message: "Event deleted successfully", type: "success" }));
      setEventToDelete(undefined);
      await fetchEvents();
    } catch (error: any) {
      dispatch(setGeneralAlertMessage({ message: error?.message || "Failed to delete event", type: "error" }));
      throw error;
    }
  };

  const filteredEvents = useMemo(() => {
    let filtered = events.filter((event) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return event.name.toLowerCase().includes(s) || event.slug.toLowerCase().includes(s);
    });

    filtered = [...filtered].sort((a, b) => {
      let aVal: any = a[sortColumn as keyof IEventListItem];
      let bVal: any = b[sortColumn as keyof IEventListItem];

      if (sortColumn === "name") { aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); }
      else if (sortColumn === "slug") { aVal = a.slug.toLowerCase(); bVal = b.slug.toLowerCase(); }
      else if (sortColumn === "isActive") { aVal = a.isActive ? 1 : 0; bVal = b.isActive ? 1 : 0; }
      else if (sortColumn === "displayOrder") { aVal = a.displayOrder; bVal = b.displayOrder; }

      return sortDirection === "asc"
        ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0)
        : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
    });

    return filtered;
  }, [events, search, sortColumn, sortDirection]);

  const pageSize = 10;
  const totalPages = Math.ceil(filteredEvents.length / pageSize);
  const paginated = filteredEvents.slice((page - 1) * pageSize, page * pageSize);

  const formatWindow = (event: IEventListItem): string => {
    if (!event.visibleFrom && !event.visibleTo) return "";
    const from = event.visibleFrom ? moment(event.visibleFrom).format("DD MMM YY") : "∞";
    const to = event.visibleTo ? moment(event.visibleTo).format("DD MMM YY") : "∞";
    return `${from} – ${to}`;
  };

  const visibilityLabel: Record<"active" | "scheduled" | "hidden", string> = {
    active: "Active",
    scheduled: "Scheduled",
    hidden: "Hidden",
  };

  const visibilityTone: Record<"active" | "scheduled" | "hidden", TagTone> = {
    active: "ok",
    scheduled: "warn",
    hidden: "danger",
  };

  return (
    <div className="events-page">
      <PageHeader
        title="Events"
        subtitle={`${filteredEvents.length} ${filteredEvents.length === 1 ? "event" : "events"}`}
        actions={
          <Button icon={<Plus size={16} />} onClick={() => navigate("/dashboard/events/new")}>
            Add Event
          </Button>
        }
      />

      {loading && events.length === 0 ? (
        <Loader />
      ) : !loading && filteredEvents.length === 0 && !search ? (
        <EmptyState
          title="No events yet"
          description="Create your first event to group tournaments, seasons, and a gallery under one brand."
          action={
            <Button icon={<Plus size={16} />} onClick={() => navigate("/dashboard/events/new")}>
              Add Event
            </Button>
          }
        />
      ) : (
        <DataTable
          id="events-table"
          data={paginated}
          editable
          label="events"
          pageNumber={page}
          pageSize={pageSize}
          totalPages={totalPages}
          totalItems={filteredEvents.length}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onViewItem={handleEdit}
          onPageChange={setPage}
          onSortChange={(col, dir) => { setSortColumn(col); setSortDirection(dir); setPage(1); }}
          canDelete
          onDelete={handleDelete}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          loading={loading}
          columns={[
            {
              key: "name", label: "Name", sortable: true,
              render: (_: any, row: IEventListItem) => (
                <div className="event-name-cell">
                  {row.logoUrl
                    ? <img src={row.logoUrl} alt={row.name} className="event-logo" />
                    : <div className="event-logo-placeholder" />
                  }
                  <span className="event-name-text">{row.name}</span>
                </div>
              ),
            },
            { key: "slug", label: "Slug", sortable: true },
            {
              key: "isActive", label: "Visibility", center: true, sortable: true,
              render: (_: any, row: IEventListItem) => {
                const status = getVisibilityStatus(row);
                const window = formatWindow(row);
                return (
                  <div className="visibility-cell">
                    <Tag tone={visibilityTone[status]}>{visibilityLabel[status]}</Tag>
                    {window && <span className="visibility-window">{window}</span>}
                  </div>
                );
              },
            },
            {
              key: "primaryColor", label: "Colors", center: true,
              render: (_: any, row: IEventListItem) => (
                <div className="color-swatches">
                  {row.primaryColor && (
                    <span
                      className="swatch"
                      style={{ backgroundColor: row.primaryColor }}
                      data-tooltip={`Primary: ${row.primaryColor}`}
                    />
                  )}
                  {row.secondaryColor && (
                    <span
                      className="swatch"
                      style={{ backgroundColor: row.secondaryColor }}
                      data-tooltip={`Secondary: ${row.secondaryColor}`}
                    />
                  )}
                </div>
              ),
            },
            {
              key: "tournamentCount", label: "Tournaments", center: true,
              render: (_: any, row: IEventListItem) => row.tournamentCount,
            },
            {
              key: "seasonCount", label: "Seasons", center: true,
              render: (_: any, row: IEventListItem) => row.seasonCount,
            },
            {
              key: "displayOrder", label: "Order", sortable: true,
              render: (_: any, row: IEventListItem) => row.displayOrder,
            },
          ]}
        />
      )}

      {eventToDelete && (
        <DeleteEventDialog
          eventName={eventToDelete.name}
          isOpen={isDeleteDialogOpen}
          onDelete={handleDeleteConfirm}
          onClose={(update) => {
            setIsDeleteDialogOpen(false);
            if (update) setEventToDelete(undefined);
          }}
        />
      )}
    </div>
  );
};

export default Events;
